import { adminTenantId, getManagementApiResourceIndicator, type TenantTag } from '@logto/schemas';
import { appendPath } from '@silverhand/essentials';
import { useMemo } from 'react';

import { adminTenantEndpoint } from '@/consts';

import { useStaticApi } from './use-api';

type TenantResponseData = {
  id: string;
  name: string;
  tag: TenantTag;
  isSuspended: boolean;
  createdAt: number;
};

/**
 * Talks to the self-hosted Tenant Management API mounted under `/me/` on the admin tenant at
 * `{ADMIN_ENDPOINT}/me/tenants`. Used in multi-tenant OSS to replace the Logto Cloud tenant
 * service. Runs against the admin tenant's `me` resource indicator, which is the same one the
 * user's profile endpoints use, so no new roles/scopes are needed.
 */
const useTenantApi = () => {
  const prefixUrl = useMemo(() => appendPath(new URL(adminTenantEndpoint), 'me'), []);

  const api = useStaticApi({
    prefixUrl,
    resourceIndicator: getManagementApiResourceIndicator(adminTenantId, 'me'),
  });

  return useMemo(
    () => ({
      list: async (): Promise<TenantResponseData[]> => api.get('tenants').json(),
      get: async (tenantId: string): Promise<TenantResponseData> =>
        api.get(`tenants/${tenantId}`).json(),
      create: async (body: { name: string; tag?: TenantTag }): Promise<TenantResponseData> =>
        api.post('tenants', { json: body }).json(),
      update: async (
        tenantId: string,
        body: { name?: string; tag?: TenantTag }
      ): Promise<TenantResponseData> => api.patch(`tenants/${tenantId}`, { json: body }).json(),
      delete: async (tenantId: string): Promise<void> => {
        await api.delete(`tenants/${tenantId}`);
      },
    }),
    [api]
  );
};

export default useTenantApi;
